<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none">
			<div class="vendor-actions">
				<v-btn
					v-if="vendorData.vendorId"
					depressed
					outlined
					color="error"
					class="mr-auto"
					@click="showDeleteDialog = true"
				>
					Delete Vendor
				</v-btn>
				<v-btn depressed outlined class="mr-2" @click="$router.push('/relationships/vendor')">
					Cancel
				</v-btn>
				<v-btn depressed outlined @click="saveVendor()"> Save Vendor </v-btn>
			</div>

			<!-- Delete Confirmation Dialog -->
			<v-dialog v-model="showDeleteDialog" max-width="450">
				<v-card>
					<v-card-title class="text-h6">Delete Vendor</v-card-title>
					<v-card-text style="font-size: 1.6rem">
						Are you sure you want to delete this vendor? This action cannot be undone.
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
						<v-btn color="error" text @click="deleteVendor()" :loading="deleting">Delete</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- Delete Record Confirmation Dialog -->
			<v-dialog v-model="showDeleteRecordDialog" max-width="450">
				<v-card>
					<v-card-title class="text-h6">Delete {{ pendingDeleteType }}</v-card-title>
					<v-card-text style="font-size: 1.6rem">
						Are you sure you want to delete this {{ pendingDeleteType.toLowerCase() }}?
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn text @click="cancelDeleteRecord()">Cancel</v-btn>
						<v-btn color="error" text @click="confirmDeleteRecord()">Delete</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- Error Snackbar -->
			<v-snackbar v-model="deleteError" color="error" :timeout="6000" top>
				<span style="font-size: 1.6rem">{{ deleteErrorMessage }}</span>
				<template v-slot:action="{ attrs }">
					<v-btn text v-bind="attrs" @click="deleteError = false">Close</v-btn>
				</template>
			</v-snackbar>
			<div class="vendor-form">
				<v-row>
					<v-col>
						<div class="form-input">
							<label>Company Name</label>
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="vendorData.companyName"
							></v-text-field>
						</div>
					</v-col>

					<v-col>
						<div class="form-input">
							<label>Vendor Number</label>
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="vendorData.vendorId"
								disabled
							></v-text-field>
						</div>
					</v-col>

					<v-col>
						<div class="form-input">
							<label>TL Account Number</label>
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="vendorData.accountNumber"
							></v-text-field>
						</div>
					</v-col>
				</v-row>
			</div>
		</v-col>

		<v-col cols="12" class="flex-auto">
			<ul class="vendor-tabs">
				<li
					class="vendor-tabs-tab"
					:class="activeTab === 1 ? 'active' : null"
					@click="activeTab = 1"
				>
					Contacts
				</li>
				<li
					class="vendor-tabs-tab"
					:class="activeTab === 2 ? 'active' : null"
					@click="activeTab = 2"
				>
					Addresses
				</li>
				<li
					class="vendor-tabs-tab"
					:class="activeTab === 3 ? 'active' : null"
					@click="activeTab = 3"
				>
					Notes
				</li>
				<li
					v-show="false"
					class="vendor-tabs-tab"
					:class="activeTab === 4 ? 'active' : null"
					@click="activeTab = 4"
				>
					Purchase Orders
				</li>
			</ul>

			<div class="table">
				<div v-if="activeTab == 1" class="table-container">
					<table>
						<thead>
							<tr>
								<th style="width: 10%" class="text-center">Primary Contact</th>
								<th style="width: 15%">Label</th>
								<th style="width: 15%">First Name</th>
								<th style="width: 15%">Last Name</th>
								<th style="width: 15%">Phone Number</th>
								<th style="width: 16%">Email address</th>
								<th style="width: 10%" class="text-center">Inactive</th>
								<th style="width: 2%">&nbsp;</th>
								<th style="width: 2%">&nbsp;</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(data, i) in contactData" :key="i">
								<template v-if="data == activeEdit">
									<td class="pl-0">
										<v-checkbox
											class="form-checkbox-center"
											v-model="data.primaryContact"
											color="accent"
											hide-details="auto"
										></v-checkbox>
									</td>
									<td>
										<div class="form-input">
											<v-select
												v-model="data.label"
												:items="contactLabels"
												dense
												outlined
												hide-details="auto"
											></v-select>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.firstName"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.lastName"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.cellPhoneNumber"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.email"
											></v-text-field>
										</div>
									</td>
									<td class="pl-0">
										<v-checkbox
											class="form-checkbox-center"
											v-model="data.isActive"
											:false-value="true"
											:true-value="false"
											color="accent"
											hide-details="auto"
										></v-checkbox>
									</td>
									<td>
										<v-btn icon color="primary" @click="activeEdit = null"
											><v-icon style="font-size: 2rem">mdi-check</v-icon>
										</v-btn>
									</td>
									<td>
										<v-btn
											icon
											color="primary"
											@click="removeContact(data)"
											><v-icon style="font-size: 2rem">mdi-delete</v-icon>
										</v-btn>
									</td>
								</template>
								<template v-else>
									<td class="pl-0">
										<v-checkbox
											disabled
											class="form-checkbox-center"
											v-model="data.primaryContact"
											color="accent"
											hide-details="auto"
											readonly
										></v-checkbox>
									</td>
									<td>
										<span v-if="data.label" class="table-label">{{ data.label }}</span>
									</td>
									<td>
										{{ data.firstName }}
									</td>
									<td>
										{{ data.lastName }}
									</td>
									<td>
										{{ data.cellPhoneNumber }}
									</td>
									<td>
										{{ data.email }}
									</td>
									<td class="pl-0">
										<v-checkbox
											class="form-checkbox-center"
											v-model="data.isActive"
											:false-value="true"
											:true-value="false"
											color="accent"
											disabled
											hide-details="auto"
											readonly
										></v-checkbox>
									</td>
									<td>
										<v-btn icon color="primary" @click="activeEdit = data"
											><v-icon style="font-size: 2rem">mdi-pencil</v-icon>
										</v-btn>
									</td>
									<td>
										<v-btn
											icon
											color="primary"
											@click="removeContact(data)"
											><v-icon style="font-size: 2rem">mdi-delete</v-icon>
										</v-btn>
									</td>
								</template>
							</tr>
							<tr>
								<td>
									<v-btn class="pl-0 pr-1" text color="accent" @click="addContact()"
										><v-icon style="font-size: 2rem">mdi-plus</v-icon> Add a contact
									</v-btn>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div v-if="activeTab == 2" class="table-container">
					<table>
						<thead>
							<tr>
								<th style="width: 12%" class="text-center">Primary Address</th>
								<th style="width: 15%">Label</th>
								<th style="width: 16%">Address Line 1</th>
								<th style="width: 16%">Address Line 2</th>
								<th style="width: 15%">City</th>
								<th style="width: 14%">State</th>
								<th style="width: 8%">Zip Code</th>
								<th style="width: 10%" class="text-center">Inactive</th>
								<th style="width: 2%">&nbsp;</th>
								<th style="width: 2%">&nbsp;</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(data, i) in addressData" :key="i">
								<template v-if="data == activeEdit">
									<td class="pl-0">
										<v-checkbox
											class="form-checkbox-center"
											v-model="data.isPrimaryAddress"
											color="accent"
											hide-details="auto"
										></v-checkbox>
									</td>
									<td>
										<div class="form-input">
											<v-select
												v-model="data.label"
												:items="addressLabels"
												dense
												outlined
												hide-details="auto"
											></v-select>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.addressLine1"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.addressLine2"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.city"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-select
												v-model="data.state"
												:items="states"
												item-text="text"
												item-value="value"
												dense
												outlined
												hide-details="auto"
											></v-select>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.postalCode"
											></v-text-field>
										</div>
									</td>
									<td class="pl-0">
										<v-checkbox
											class="form-checkbox-center"
											v-model="data.isActive"
											color="accent"
											hide-details="auto"
										></v-checkbox>
									</td>
									<td>
										<v-btn icon color="primary" @click="activeEdit = null"
											><v-icon style="font-size: 2rem">mdi-check</v-icon>
										</v-btn>
									</td>
									<td>
										<v-btn
											icon
											color="primary"
											@click="removeAddress(data)"
											><v-icon style="font-size: 2rem">mdi-delete</v-icon>
										</v-btn>
									</td>
								</template>
								<template v-else>
									<td class="pl-0">
										<v-checkbox
											disabled
											class="form-checkbox-center"
											v-model="data.isPrimaryAddress"
											color="accent"
											hide-details="auto"
											readonly
										></v-checkbox>
									</td>
									<td>
										<span v-if="data.label" class="table-label">{{ data.label }}</span>
									</td>
									<td>
										{{ data.addressLine1 }}
									</td>
									<td>
										{{ data.addressLine2 }}
									</td>
									<td>
										{{ data.city }}
									</td>
									<td>
										{{ data.state }}
									</td>
									<td>
										{{ data.postalCode }}
									</td>
									<td class="pl-0">
										<v-checkbox
											disabled
											class="form-checkbox-center"
											v-model="data.isActive"
											color="accent"
											hide-details="auto"
											readonly
										></v-checkbox>
									</td>
									<td>
										<v-btn icon color="primary" @click="activeEdit = data"
											><v-icon style="font-size: 2rem">mdi-pencil</v-icon>
										</v-btn>
									</td>
									<td>
										<v-btn
											icon
											color="primary"
											@click="removeAddress(data)"
											><v-icon style="font-size: 2rem">mdi-delete</v-icon>
										</v-btn>
									</td>
								</template>
							</tr>

							<tr>
								<td>
									<v-btn class="pl-0 pr-1" text color="accent" @click="addAddress()"
										><v-icon style="font-size: 2rem">mdi-plus</v-icon> Add an address
									</v-btn>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div v-if="activeTab == 3" class="table-container">
					<table>
						<thead>
							<tr>
								<th style="width: 60%">Note</th>
								<th style="width: 20%">Added By</th>
								<th style="width: 16%">Date/Time</th>
								<th style="width: 2%">&nbsp;</th>
								<th style="width: 2%">&nbsp;</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(data, i) in noteData" :key="i">
								<template v-if="data == activeEdit">
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.note"
											></v-text-field>
										</div>
									</td>
									<td>
										<p>{{ data.addedBy }}</p>
									</td>
									<td>
										<p>{{ formatDateLocal(data.date) }}</p>
									</td>
									<td>
										<v-btn icon color="primary" @click="activeEdit = null"
											><v-icon style="font-size: 2rem">mdi-check</v-icon>
										</v-btn>
									</td>
									<td>
										<v-btn icon color="primary" @click="removeNote(data)"
											><v-icon style="font-size: 2rem">mdi-delete</v-icon>
										</v-btn>
									</td>
								</template>
								<template v-else>
									<td>
										{{ data.note }}
									</td>
									<td>
										<p>{{ data.addedBy }}</p>
									</td>
									<td>
										<p>{{ formatDateLocal(data.date) }}</p>
									</td>
									<td>
										<v-btn icon color="primary" @click="activeEdit = data"
											><v-icon style="font-size: 2rem">mdi-pencil</v-icon>
										</v-btn>
									</td>
									<td>
										<v-btn icon color="primary" @click="removeNote(data)"
											><v-icon style="font-size: 2rem">mdi-delete</v-icon>
										</v-btn>
									</td>
								</template>
							</tr>
							<tr>
								<td>
									<v-btn class="pl-0 pr-1" text color="accent" @click="addNote()"
										><v-icon style="font-size: 2rem">mdi-plus</v-icon> Add a note
									</v-btn>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div v-if="activeTab == 4" class="table-container">
					<table>
						<thead>
							<tr>
								<th style="width: 13%">Cus Order #</th>
								<th style="width: 8%">PO #</th>
								<th style="width: 7%">Line #</th>
								<th style="width: 11%">Order Date</th>
								<th style="width: 10%">Style</th>
								<th style="width: 12%" class="text-center">Order Status</th>
								<th style="width: 9%">REF</th>
								<th style="width: 9%">Process</th>
								<th style="width: 7%">Ordered</th>
								<th style="width: 7%">Received</th>
								<th style="width: 7%">BO</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(customer, i) in orderData" :key="i">
								<td>{{ customer.orderNumber }}</td>
								<td>{{ customer.poNumber }}</td>
								<td>{{ customer.lineNumber }}</td>
								<td>{{ customer.orderDate }}</td>
								<td>{{ customer.style }}</td>
								<td class="text-center">
									<div class="table-status">
										{{ customer.status }}
									</div>
								</td>
								<td>{{ customer.ref }}</td>
								<td>{{ customer.process }}</td>
								<td>{{ customer.ordered }}</td>
								<td>{{ customer.received }}</td>
								<td>{{ customer.bo }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</v-col>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import DateFormatting from "@/mixins/DateFormatting";
import { getApiUrl } from "@/helpers";
export default {
	name: "VendorView",
	mixins: [DateFormatting],
	props: {
		vendorId: {
			type: Number,
			default: null,
		},
	},
	data() {
		return {
			activeEdit: null,
			vendorData: {
				vendorId: null,
				companyName: "",
				accountNumber: null,
			},
			activeTab: 1,
			contactData: [],
			contactLabels: ["CUSTOMER SERVICE", "OUTSIDE SALES REP", "ACCOUNTS PAYABLE"],
			addressData: [],
			addressLabels: ["WAREHOUSE", "ACCOUNTS PAYABLE", "FACTORY"],
			noteData: [],
			orderData: [],
			showDeleteDialog: false,
			deleting: false,
			deleteError: false,
			deleteErrorMessage: "",
			showDeleteRecordDialog: false,
			pendingDeleteData: null,
			pendingDeleteType: "",
			states: [
				{ value: "AL", text: "Alabama" },
				{ value: "AK", text: "Alaska" },
				{ value: "AZ", text: "Arizona" },
				{ value: "AR", text: "Arkansas" },
				{ value: "CA", text: "California" },
				{ value: "CO", text: "Colorado" },
				{ value: "CT", text: "Connecticut" },
				{ value: "DE", text: "Delaware" },
				{ value: "DC", text: "District Of Columbia" },
				{ value: "FL", text: "Florida" },
				{ value: "GA", text: "Georgia" },
				{ value: "HI", text: "Hawaii" },
				{ value: "ID", text: "Idaho" },
				{ value: "IL", text: "Illinois" },
				{ value: "IN", text: "Indiana" },
				{ value: "IA", text: "Iowa" },
				{ value: "KS", text: "Kansas" },
				{ value: "KY", text: "Kentucky" },
				{ value: "LA", text: "Louisiana" },
				{ value: "ME", text: "Maine" },
				{ value: "MD", text: "Maryland" },
				{ value: "MA", text: "Massachusetts" },
				{ value: "MI", text: "Michigan" },
				{ value: "MN", text: "Minnesota" },
				{ value: "MS", text: "Mississippi" },
				{ value: "MO", text: "Missouri" },
				{ value: "MT", text: "Montana" },
				{ value: "NE", text: "Nebraska" },
				{ value: "NV", text: "Nevada" },
				{ value: "NH", text: "New Hampshire" },
				{ value: "NJ", text: "New Jersey" },
				{ value: "NM", text: "New Mexico" },
				{ value: "NY", text: "New York" },
				{ value: "NC", text: "North Carolina" },
				{ value: "ND", text: "North Dakota" },
				{ value: "OH", text: "Ohio" },
				{ value: "OK", text: "Oklahoma" },
				{ value: "OR", text: "Oregon" },
				{ value: "PA", text: "Pennsylvania" },
				{ value: "RI", text: "Rhode Island" },
				{ value: "SC", text: "South Carolina" },
				{ value: "SD", text: "South Dakota" },
				{ value: "TN", text: "Tennessee" },
				{ value: "TX", text: "Texas" },
				{ value: "UT", text: "Utah" },
				{ value: "VT", text: "Vermont" },
				{ value: "VA", text: "Virginia" },
				{ value: "WA", text: "Washington" },
				{ value: "WV", text: "West Virginia" },
				{ value: "WI", text: "Wisconsin" },
				{ value: "WY", text: "Wyoming" },
			],
		};
	},
	methods: {
		addContact() {
			const newContact = {
				vendorContactId: null,
				label: "",
				firstName: "",
				lastName: "",
				cellPhoneNumber: "",
				email: "",
				primaryContact: false,
				isActive: true,
			};

			this.contactData.push(newContact);

			//set the new contact as the active edit
			this.activeEdit = newContact;
		},

		removeContact(data) {
			if (data.vendorContactId) {
				this.pendingDeleteData = data;
				this.pendingDeleteType = "Contact";
				this.showDeleteRecordDialog = true;
			} else {
				this.contactData = this.contactData.filter((obj) => obj !== data);
			}
		},

		addAddress() {
			const newAddress = {
				vendorAddressId: null,
				isPrimaryAddress: false,
				label: "",
				addressLine1: "",
				addressLine2: "",
				city: "",
				state: "",
				postalCode: "",
				isActive: true,
			};
			this.addressData.push(newAddress);
			//set the new contact as the active edit
			this.activeEdit = newAddress;
		},

		removeAddress(data) {
			if (data.vendorAddressId) {
				this.pendingDeleteData = data;
				this.pendingDeleteType = "Address";
				this.showDeleteRecordDialog = true;
			} else {
				this.addressData = this.addressData.filter((obj) => obj !== data);
			}
		},

		addNote() {
			const newNote = {
				vendorNoteId: this.noteData.length,
				note: "",
				addedBy: this.$store.getters.userInfo.name,
				date: new Date(),
			};

			this.noteData.push(newNote);

			//set the new contact as the active edit
			this.activeEdit = newNote;
		},

		removeNote(data) {
			if (data.vendorNoteId) {
				this.pendingDeleteData = data;
				this.pendingDeleteType = "Note";
				this.showDeleteRecordDialog = true;
			} else {
				this.noteData = this.noteData.filter((obj) => obj !== data);
			}
		},

		cancelDeleteRecord() {
			this.showDeleteRecordDialog = false;
			this.pendingDeleteData = null;
			this.pendingDeleteType = "";
		},

		async confirmDeleteRecord() {
			const data = this.pendingDeleteData;
			const type = this.pendingDeleteType;
			this.showDeleteRecordDialog = false;

			try {
				if (type === "Contact") {
					await this.$http.delete(`/api/vendors/contacts/${data.vendorContactId}`);
					this.contactData = this.contactData.filter((obj) => obj !== data);
				} else if (type === "Address") {
					await this.$http.delete(`/api/vendors/addresses/${data.vendorAddressId}`);
					this.addressData = this.addressData.filter((obj) => obj !== data);
				} else if (type === "Note") {
					await this.$http.delete(`/api/vendors/notes/${data.vendorNoteId}`);
					this.noteData = this.noteData.filter((obj) => obj !== data);
				}
			} catch (error) {
				this.deleteErrorMessage = `Failed to delete ${type.toLowerCase()}.`;
				this.deleteError = true;
			}

			this.pendingDeleteData = null;
			this.pendingDeleteType = "";
		},

		async deleteVendor() {
			this.deleting = true;
			try {
				await this.$http.delete(`/api/vendors/${this.vendorData.vendorId}`);
				this.showDeleteDialog = false;
				this.$router.push("/relationships/vendor");
			} catch (error) {
				this.showDeleteDialog = false;
				this.deleteErrorMessage =
					error.response?.data?.error || "Failed to delete vendor.";
				this.deleteError = true;
			} finally {
				this.deleting = false;
			}
		},

		async saveVendor() {
			try {
				let res = this.vendorData.vendorId
					? await this.$http.put(`/api/vendors`, this.vendorData)
					: await this.$http.post(`/api/vendors`, this.vendorData);
				console.log(res);

				//add each vendor related field (contacts, addresses, notes) to the vendor
				let vendorId = this.vendorData.vendorId ?? res.data.vendorId;
				if (vendorId) {
					await this.contactData.forEach((contact) =>
						this.saveContact({ vendorId: vendorId, ...contact })
					);
					await this.addressData.forEach((address) =>
						this.saveAddress({ vendorId: vendorId, ...address })
					);
					await this.noteData.forEach((note) => this.saveNote({ vendorId: vendorId, ...note }));
				}

				setTimeout(() => {
					this.$router.back();
				}, 200);
			} catch (error) {
				console.log(error);
			}
		},

		async saveContact(contact) {
			try {
				//check if contact has an id and either create or update the contact
				if (contact.vendorContactId) {
					await this.$http.put(getApiUrl("/api/vendors/contacts"), contact);
				} else {
					await this.$http.post(getApiUrl("/api/vendors/contacts"), contact);
				}
			} catch (error) {
				console.log(error);
			}
		},

		async saveAddress(address) {
			try {
				//check if address has an id and either create or update the address
				if (address.vendorAddressId) {
					await this.$http.put(getApiUrl("/api/vendors/addresses"), address);
				} else {
					await this.$http.post(getApiUrl("/api/vendors/addresses"), address);
				}
			} catch (error) {
				console.log(error);
			}
		},

		async saveNote(note) {
			try {
				//check if note has a vendorNoteId and either create or update the note
				if (note.vendorNoteId) {
					await this.$http.put(getApiUrl("/api/vendors/notes"), note);
				} else {
					await this.$http.post(getApiUrl("/api/vendors/notes"), note);
				}
			} catch (error) {
				console.log(error);
			}
		},

		async getVendor() {
			try {
				const res = await this.$apollo.query({
					query: gql`query {
                        vendorById(id: ${this.vendorId}) {
                            vendorId
                            companyName
                            accountNumber
                            contacts { 
                                vendorContactId
                                label
                                firstName
                                lastName
                                cellPhoneNumber
                                email
                                primaryContact
                                isActive
                            }
                            addresses {
                                vendorAddressId
                                isPrimaryAddress
                                label
                                addressLine1
                                addressLine2
                                city
                                state
                                postalCode
								isActive
                            }
                            notes {
                                vendorNoteId
                                note
                                addedBy
                                date
                            }
                        }
                    }`,
					fetchPolicy: "no-cache",
				});

				console.log(res);
				this.vendorData = res.data.vendorById;
				this.contactData = res.data.vendorById.contacts ?? [];
				this.addressData = res.data.vendorById.addresses ?? [];
				this.noteData = res.data.vendorById.notes ?? [];
			} catch (error) {
				console.log(error);
			}
		},
	},
	async mounted() {
		if (this.vendorId) {
			await this.getVendor();
		}
	},
};
</script>

<style lang="scss" scoped>
.vendor {
	&-actions {
		display: flex;
		width: 100%;
		justify-content: flex-end;
		margin-bottom: 1rem;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			text-transform: initial;
		}
	}

	&-form {
		position: relative;
		display: block;
		background-color: $color-white;
		border: 1px solid $color-gray;
		border-radius: 5px;
		padding: 0.5rem 1.5rem;
	}

	&-tabs {
		list-style: none;
		display: flex;
		flex-flow: row nowrap;
		padding-left: 0;

		&-tab {
			padding: 0.5rem 1rem;
			color: $color-gray;
			border-bottom: 2px solid transparent;
			cursor: pointer;
			transition: all 0.3s;

			&:hover {
				border-bottom: 2px solid $color-gray;
			}

			&.active {
				color: $color-black;
				border-bottom: 2px solid $color-tertiary;
			}
		}
	}
}
</style>
